import { Text } from '@backstage/ui';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { Meter } from '../../home/Meter';
import { formatCpu, formatMemory } from '../../waadooForm/formats';
import {
  quotaGauges,
  type EnvironmentQuotaReading,
  type QuotaGauge,
} from '../configuration';
import { useUsageFormat } from '../ResourceBits';
import { projectEnvironmentsTranslationRef } from '../translation';

/**
 * Each gauge's visible label, short under the « Quota » label, and its
 * accessible name, whole (« Quota CPU réservé ») so that it is not taken for
 * the fields of the same name.
 */
const GAUGE_KEYS = {
  cpu: ['configuration.quotaCpuShort', 'configuration.quotaCpu'],
  memory: ['configuration.quotaMemoryShort', 'configuration.quotaMemory'],
  pods: ['configuration.quotaPods', 'configuration.quotaPods'],
} as const satisfies Record<QuotaGauge['resource'], readonly [string, string]>;

const NUMBER = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

/**
 * The live quota of the namespace as three bars — reserved CPU, reserved
 * memory, pods: used against hard (`quotaGauges`). Without a readable one,
 * a sentence instead — asleep, the namespace does not exist —, never a bar
 * on an invented figure.
 */
export function QuotaGauges(props: {
  quota: EnvironmentQuotaReading | undefined;
  sleeping: boolean;
  branch: string;
}) {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const format = useUsageFormat();
  const gauges = quotaGauges(props.quota);
  if (gauges.length === 0) {
    return (
      <Text as="p" variant="body-small" color="secondary">
        {props.sleeping
          ? t('configuration.quotaAsleep', { branch: props.branch })
          : t('configuration.quotaMissing')}
      </Text>
    );
  }
  const figure = (gauge: QuotaGauge, value: number) => {
    if (gauge.resource === 'cpu') return formatCpu(value);
    if (gauge.resource === 'memory') return formatMemory(value);
    return NUMBER.format(value);
  };
  return (
    <div className="waadoo-config__gauges">
      {gauges.map(gauge => {
        const [label, accessibleLabel] = GAUGE_KEYS[gauge.resource];
        return (
          <Meter
            key={gauge.resource}
            label={t(label)}
            accessibleLabel={t(accessibleLabel)}
            valueText={`${figure(gauge, gauge.used)} / ${figure(
              gauge,
              gauge.hard,
            )}`}
            ratio={gauge.used / gauge.hard}
            percentText={format.percent(gauge.used / gauge.hard)}
          />
        );
      })}
    </div>
  );
}
